import React from 'react';
import { Alert, Button, Form, Input, Space } from 'antd';
import { PlusOutlined } from '@ant-design/icons';

import { CustomerGroupApiType, CustomerGroupBodyType } from '../types';
import { FORM_RULES } from 'src/constants';
import { Store } from 'antd/es/form/interface';
import { MediaUploader, PlacesAutocomplete } from 'src/ui';
import { CustomerParentGroupsSelect } from 'src/ui/CustomerParentGroupsSelect/CustomerParentGroupsSelect';

import { UploadedMediaType } from 'src/ui/MediaUploader/types';

type FormViewProps = {
  loading: boolean;
  group?: CustomerGroupApiType;
  customer?: any;
  isCreate?: boolean;
  onSubmit: (body: CustomerGroupBodyType) => void;
};

const { TextArea } = Input;

export const FormView: React.FC<FormViewProps> = ({
  loading,
  group,
  isCreate,
  customer,
  onSubmit,
}) => {
  const [form] = Form.useForm();
  const defaultCountry = customer?.places[0]?.address?.country || 'France';

  const submit = (values: Store) => {
    const data: CustomerGroupBodyType = {
      name: values.name,
      places: [values.place],
      description: values.description,
      avatar: { id: values.avatar[0]?.id },
      cover: { id: values.cover[0]?.id },
    };

    if (values.parent) data.parent = { id: values.parent };

    if (values.parent === 'detach') data.parent = null;

    onSubmit(data);
  };

  const avatar = group?.avatar?.url
    ? [
        {
          uid: group.avatar.id,
          name: group.avatar.name,
          url: group.avatar.url.original,
          type: group.avatar.model_type,
          id: group.avatar.id,
        },
      ]
    : [];

  const cover = group?.cover?.url
    ? [
        {
          uid: group.cover.id,
          name: group.cover.name,
          url: group.cover.url.original,
          type: group.cover.model_type,
          id: group.cover.id,
        },
      ]
    : [];

  const initialValues = {
    name: group ? group.name : '',
    place: group ? group.places[0]?.address?.formatted_address : '',
    description: group ? group?.description : '',
    parent: group ? group?.parent?.id : undefined,
    avatar: avatar,
    cover: cover,
  };

  return (
    <Form
      id="groupForm"
      form={form}
      onFinish={submit}
      initialValues={initialValues}
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <Form.Item name="name" rules={[FORM_RULES.required]}>
          <Input
            placeholder="Nom de la communauté"
            id="name"
            value={initialValues.name}
          />
        </Form.Item>
        <Form.Item name="place" rules={[FORM_RULES.required]}>
          <PlacesAutocomplete
            defaultCountry={defaultCountry}
            placeholder="Adresse de la communauté"
            id="placeId"
            onSelectChange={place =>
              form.setFieldsValue({ place: place?.place_id })
            }
            defaultValue={initialValues.place}
          />
        </Form.Item>
        <Form.Item name="description" rules={[FORM_RULES.required]}>
          <TextArea
            placeholder="Description de la communauté"
            id="name"
            value={initialValues.description}
          />
        </Form.Item>
        {!group?.children.length && (
          <Form.Item name="parent">
            <CustomerParentGroupsSelect
              placeholder="Ajouter à une communauté parente"
              id="parent"
              name="parent"
              customerId={customer?.id}
              defaultValue={initialValues.parent}
              onChange={(parent: string) =>
                form.setFieldsValue({ parent: parent })
              }
            />
          </Form.Item>
        )}
        <Form.Item name="avatar" rules={[FORM_RULES.required]}>
          <MediaUploader
            limit={1}
            placeholder="Télécharger l'avatar de la communauté"
            pictureListType="picture-card"
            canRemove
            defaultValue={initialValues.avatar}
            onUpload={(avatar: Array<UploadedMediaType>) => {
              form.setFieldsValue({
                avatar,
              });
            }}
          >
            <Button id="media" icon={<PlusOutlined />}></Button>
          </MediaUploader>
        </Form.Item>
        <Form.Item name="cover" rules={[FORM_RULES.required]}>
          <MediaUploader
            limit={1}
            placeholder="Télécharger la photo de couverture de la communauté"
            pictureListType="picture-card"
            canRemove
            defaultValue={initialValues.cover}
            onUpload={(cover: Array<UploadedMediaType>) => {
              form.setFieldsValue({
                cover,
              });
            }}
          >
            <Button id="media" icon={<PlusOutlined />}></Button>
          </MediaUploader>
        </Form.Item>

        {isCreate && (
          <Alert
            style={{ marginBottom: '15px' }}
            message="Pour afficher la communauté côté client, pensez à ajouter un Administrateur après l’enregistrement."
            type="warning"
          />
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            id="submit"
            htmlType="submit"
            type="primary"
            loading={loading}
          >
            Enregistrer
          </Button>
        </div>
      </Space>
    </Form>
  );
};
